/*
 * Recovery codes panel — shown once after a user enrols their first 2FA method
 * (and when codes are regenerated). One-time backup codes that let a user back
 * into their account if they lose their authenticator / phone.
 *
 * UX is deliberately "ceremonial": the codes are shown once, the user must copy
 * or share them and explicitly confirm they saved them before the Done button
 * unlocks — mirroring how Google / GitHub / 1Password present backup codes.
 */

import React, { useState } from 'react';
import {
    ActivityIndicator,
    Share,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import { COLORS } from './TwoFactorUI';
import { useManagementTheme } from '../../helpers/managementTheme';

/**
 * @param {object}   props
 * @param {string[]} props.codes       The one-time recovery codes (plaintext, shown once).
 * @param {Function} props.t           i18n translate function.
 * @param {Function} props.onDone      Called when the user confirms they saved the codes.
 * @param {string}   [props.doneLabel] Override for the primary button label.
 * @param {boolean}  [props.busy]      Show a spinner on the primary button.
 */
export default function RecoveryCodes({
    codes = [],
    t,
    onDone,
    doneLabel,
    busy = false,
}) {
    // Rendered inside the (theme-aware) PremiumSheet, so text/surfaces follow the theme.
    const m = useManagementTheme();
    const [copied, setCopied] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);

    const asText = codes.join('\n');

    const handleCopy = async () => {
        try {
            await Clipboard.setStringAsync(asText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch (_e) {
            // Clipboard can be unavailable (e.g. restricted web context) — Share is the fallback.
        }
    };

    const handleShare = async () => {
        try {
            await Share.share({
                title: t('two_factor_recovery_codes_title', { defaultValue: 'Recovery codes' }),
                message: `${t('two_factor_recovery_codes_share_intro', { defaultValue: 'Happy Solutions recovery codes — keep these safe:' })}\n\n${asText}`,
            });
        } catch (_e) {
            // User dismissed the share sheet — no-op.
        }
    };

    return (
        <View style={styles.wrap}>
            <View style={styles.iconBadge}>
                <Ionicons name="key" size={26} color={COLORS.brandDark} />
            </View>
            <Text style={[styles.title, { color: m.colors.heading }]}>
                {t('two_factor_recovery_codes_save_title', { defaultValue: 'Save your recovery codes' })}
            </Text>
            <Text style={[styles.subtitle, { color: m.colors.subText }]}>
                {t('two_factor_recovery_codes_help', { defaultValue: 'Use one of these one-time codes to sign in if you lose access to your other methods. Store them somewhere safe — they are shown only once.' })}
            </Text>

            <View style={styles.grid}>
                {codes.map((code, idx) => (
                    <View key={code} style={[styles.codeChip, { backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border }]}>
                        <Text style={[styles.codeIndex, { color: m.colors.muted }]}>{String(idx + 1).padStart(2, '0')}</Text>
                        <Text style={[styles.codeText, { color: m.colors.heading }]} selectable>{code}</Text>
                    </View>
                ))}
            </View>

            <View style={styles.actionsRow}>
                <TouchableOpacity style={[styles.secondaryBtn, { backgroundColor: m.colors.surface, borderColor: m.colors.border }]} onPress={handleCopy} activeOpacity={0.85}>
                    <Ionicons name={copied ? 'checkmark-circle' : 'copy-outline'} size={17} color={copied ? COLORS.success : m.colors.heading} />
                    <Text style={[styles.secondaryText, { color: m.colors.heading }, copied ? { color: COLORS.success } : null]}>
                        {copied
                            ? t('copied', { defaultValue: 'Copied' })
                            : t('two_factor_recovery_copy_all', { defaultValue: 'Copy all' })}
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.secondaryBtn, { backgroundColor: m.colors.surface, borderColor: m.colors.border }]} onPress={handleShare} activeOpacity={0.85}>
                    <Ionicons name="share-outline" size={17} color={m.colors.heading} />
                    <Text style={[styles.secondaryText, { color: m.colors.heading }]}>{t('two_factor_recovery_share', { defaultValue: 'Share' })}</Text>
                </TouchableOpacity>
            </View>

            <View style={[styles.warning, { backgroundColor: m.colors.chipBg, borderColor: m.colors.border }]}>
                <Ionicons name="warning-outline" size={16} color={COLORS.brandDark} />
                <Text style={styles.warningText}>
                    {t('two_factor_recovery_codes_warning', { defaultValue: 'Each code works once. Anyone with these codes can access your account.' })}
                </Text>
            </View>

            <TouchableOpacity
                style={styles.ackRow}
                onPress={() => setAcknowledged((v) => !v)}
                activeOpacity={0.8}
            >
                <View style={[styles.checkbox, { backgroundColor: m.colors.surface, borderColor: m.colors.inputBorder }, acknowledged ? styles.checkboxOn : null]}>
                    {acknowledged && <Ionicons name="checkmark" size={15} color="#fff" />}
                </View>
                <Text style={[styles.ackText, { color: m.colors.text }]}>
                    {t('two_factor_recovery_codes_ack', { defaultValue: 'I have saved my recovery codes in a safe place' })}
                </Text>
            </TouchableOpacity>

            <TouchableOpacity
                style={[styles.primary, (!acknowledged || busy) ? styles.primaryDisabled : null]}
                onPress={onDone}
                disabled={!acknowledged || busy}
                activeOpacity={0.9}
            >
                {busy
                    ? <ActivityIndicator color="#fff" />
                    : (
                        <Text style={styles.primaryText}>
                            {doneLabel || t('two_factor_recovery_done', { defaultValue: 'Done' })}
                        </Text>
                    )}
            </TouchableOpacity>
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: {
        alignItems: 'center',
    },
    iconBadge: {
        width: 56,
        height: 56,
        borderRadius: 20,
        backgroundColor: COLORS.tint,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
    },
    title: {
        fontSize: 19,
        fontWeight: '800',
        color: COLORS.ink,
        textAlign: 'center',
    },
    subtitle: {
        marginTop: 8,
        fontSize: 13,
        lineHeight: 19,
        color: COLORS.sub,
        textAlign: 'center',
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        marginTop: 18,
        marginBottom: 4,
        width: '100%',
    },
    codeChip: {
        width: '48.5%',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: COLORS.soft,
        borderWidth: 1,
        borderColor: COLORS.line,
        borderRadius: 16,
        paddingVertical: 11,
        paddingHorizontal: 12,
        marginBottom: 10,
    },
    codeIndex: {
        fontSize: 11,
        fontWeight: '700',
        color: COLORS.sub,
    },
    codeText: {
        flex: 1,
        fontSize: 15,
        fontWeight: '700',
        letterSpacing: 1,
        color: COLORS.ink,
        fontVariant: ['tabular-nums'],
    },
    actionsRow: {
        flexDirection: 'row',
        gap: 10,
        marginTop: 6,
        marginBottom: 14,
        width: '100%',
    },
    secondaryBtn: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        paddingVertical: 12,
        borderRadius: 999,
        borderWidth: 1.5,
        borderColor: COLORS.line,
        backgroundColor: '#fff',
    },
    secondaryText: {
        fontSize: 14,
        fontWeight: '700',
        color: COLORS.ink,
    },
    warning: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
        backgroundColor: COLORS.tint,
        borderWidth: 1,
        borderColor: '#FED7AA',
        borderRadius: 18,
        padding: 12,
        marginBottom: 16,
        width: '100%',
    },
    warningText: {
        flex: 1,
        fontSize: 12,
        lineHeight: 17,
        color: COLORS.brandDark,
        fontWeight: '600',
    },
    ackRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
        width: '100%',
    },
    checkbox: {
        width: 24,
        height: 24,
        borderRadius: 7,
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
    },
    checkboxOn: {
        backgroundColor: COLORS.brand,
        borderColor: COLORS.brand,
    },
    ackText: {
        flex: 1,
        fontSize: 13.5,
        fontWeight: '600',
        color: COLORS.ink,
    },
    primary: {
        width: '100%',
        backgroundColor: COLORS.brand,
        borderRadius: 999,
        paddingVertical: 15,
        alignItems: 'center',
        shadowColor: COLORS.brandDark,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.3,
        shadowRadius: 14,
        elevation: 6,
    },
    primaryDisabled: {
        opacity: 0.5,
    },
    primaryText: {
        color: '#fff',
        fontWeight: '800',
        fontSize: 15,
    },
});
